import {
  FormEvent,
  memo,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Helmet } from "react-helmet";
import { useSearchParams } from "react-router-dom";
import { API_BASE } from "../../../API";
import {
  ASK_LABELS,
  AskStatus,
  PRIORITY_RANK,
  colors,
  fontFamily,
  type,
} from "./styles";

// Matches MARKIT_FUNDRAISING_ACCESS_KEY in MarkitBackEnd's api/.env.
// This is a lightweight speed bump, not real security — it ships in this
// built JS bundle and is visible in the Network tab to anyone who opens the
// page, on purpose (this page is meant to be reachable by anyone with the
// link). See this folder's README.
const ACCESS_KEY = "TOu8Ky9BNi4NBm5mX5C8HRcr";

// Same lightweight-speed-bump caveat as ACCESS_KEY above — a hardcoded
// client-side password, not real auth. Just enough to stop a link from
// being casually stumbled into; remembered per-browser via localStorage so
// someone with the link doesn't have to retype it every visit.
const PAGE_PASSWORD = "markit";
const UNLOCK_STORAGE_KEY = "markit-fundraising-unlocked";

const ALL_PRIORITIES = Object.keys(PRIORITY_RANK);
const DEFAULT_PRIORITIES = ["Essential", "Very High", "High", "Medium"];

type NamedContact = {
  name: string;
  introSource: string[];
};

type FundEntry = {
  name: string;
  city: string;
  priority: string;
  ask: AskStatus;
  rationale: string;
  namedContacts: NamedContact[];
  generalIntroSource: string[];
};

type AngelEntry = {
  name: string;
  org: string;
  city: string;
  priority: string;
  ask: AskStatus;
  rationale: string;
  introSource: string[];
};

type DirectoryResponse = {
  funds: FundEntry[];
  angels: AngelEntry[];
};

type View = "funds" | "angels";

function sortByPriority<T extends { priority: string }>(entries: T[]): T[] {
  return [...entries].sort(
    (a, b) => (PRIORITY_RANK[b.priority] ?? 0) - (PRIORITY_RANK[a.priority] ?? 0)
  );
}

const priorityDots = (priorityTier: string) => {
  const rank = PRIORITY_RANK[priorityTier] ?? 1;
  const maxRank = Math.max(...Object.values(PRIORITY_RANK));
  return (
    <div style={{ display: "flex", gap: 2 }}>
      {Array.from({ length: maxRank }).map((_, i) => (
        <span
          key={i}
          style={{
            width: 6,
            height: 6,
            borderRadius: 1,
            background: i < rank ? colors.ink : colors.borderStrong,
          }}
        />
      ))}
    </div>
  );
};

const AskPill = ({ ask }: { ask: AskStatus }) => {
  const tone =
    ask === "looking"
      ? { color: colors.looking, bg: colors.lookingBg }
      : ask === "have"
        ? { color: colors.have, bg: colors.haveBg }
        : { color: colors.covered, bg: colors.coveredBg };
  return (
    <span
      style={{
        ...type.bodySmallMedium,
        color: tone.color,
        background: tone.bg,
        padding: "2px 9px",
        borderRadius: 999,
        whiteSpace: "nowrap",
      }}
    >
      {ASK_LABELS[ask]}
    </span>
  );
};

const EntryCard = ({
  name,
  meta,
  ask,
  priority,
  rationale,
  children,
}: {
  name: string;
  meta: string;
  ask: AskStatus;
  priority: string;
  rationale: string;
  children?: ReactNode;
}) => (
  <div
    style={{
      border: `1px solid ${colors.border}`,
      borderRadius: 10,
      background: colors.surface,
      padding: "16px 18px",
      boxShadow: "0 1px 2px rgba(28,28,29,0.05), 0 6px 20px rgba(28,28,29,0.07)",
    }}
  >
    <div
      className="AlignedRowSpaced"
      style={{ alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}
    >
      <div style={{ minWidth: 0 }}>
        <p style={{ ...type.sectionTitle, margin: 0 }}>{name}</p>
        {meta ? (
          <p style={{ ...type.bodySmallRegular, color: colors.inkMuted, margin: "2px 0 0" }}>
            {meta}
          </p>
        ) : null}
      </div>
      <div
        className="ColumnNormal"
        style={{ alignItems: "flex-end", gap: 6, flexShrink: 0 }}
      >
        <AskPill ask={ask} />
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ ...type.bodySmallMedium, color: colors.inkMuted }}>
            {priority}
          </span>
          {priorityDots(priority)}
        </div>
      </div>
    </div>
    <p style={{ ...type.bodyRegular, color: colors.ink, marginTop: 10, maxWidth: 640 }}>
      {rationale}
    </p>
    {children}
  </div>
);

const MarkitFundraisingScreen = memo(function MarkitFundraisingScreenFn() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView: View = searchParams.get("view") === "angels" ? "angels" : "funds";

  const [view, setView] = useState<View>(initialView);
  const [search, setSearch] = useState("");
  const [activeAsk, setActiveAsk] = useState<AskStatus | null>(null);
  const [activePriorities, setActivePriorities] = useState<Set<string>>(
    new Set(DEFAULT_PRIORITIES)
  );
  const [data, setData] = useState<DirectoryResponse | null>(null);
  const [loadError, setLoadError] = useState(false);

  const [unlocked, setUnlocked] = useState(() => {
    try {
      return localStorage.getItem(UNLOCK_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState(false);

  const handleUnlockSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (passwordInput === PAGE_PASSWORD) {
      setUnlocked(true);
      setPasswordError(false);
      try {
        localStorage.setItem(UNLOCK_STORAGE_KEY, "true");
      } catch {
        // localStorage unavailable (private browsing, etc.) — fine, they'll
        // just re-enter the password next visit.
      }
    } else {
      setPasswordError(true);
    }
  };

  useEffect(() => {
    if (!unlocked) return;
    fetch(`${API_BASE}public/markitFundraising/directory?key=${ACCESS_KEY}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        return res.json();
      })
      .then((json: DirectoryResponse) => {
        setData({
          funds: sortByPriority(json.funds),
          angels: sortByPriority(json.angels),
        });
      })
      .catch((err) => {
        console.error("markit-fundraising: failed to load directory", err);
        setLoadError(true);
      });
  }, [unlocked]);

  const changeView = (nextView: View) => {
    setView(nextView);
    setSearch("");
    setActiveAsk(null);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("view", nextView);
    setSearchParams(nextParams, { replace: true });
  };

  const togglePriority = (tier: string) => {
    setActivePriorities((prev) => {
      const next = new Set(prev);
      if (next.has(tier)) {
        next.delete(tier);
      } else {
        next.add(tier);
      }
      return next;
    });
  };

  const filteredFunds = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.funds.filter((f) => {
      const matchesText = !q || `${f.name} ${f.city}`.toLowerCase().includes(q);
      const matchesAsk = !activeAsk || f.ask === activeAsk;
      const matchesPriority = activePriorities.has(f.priority);
      return matchesText && matchesAsk && matchesPriority;
    });
  }, [data, search, activeAsk, activePriorities]);

  const filteredAngels = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.angels.filter((a) => {
      const matchesText =
        !q || `${a.name} ${a.org} ${a.city}`.toLowerCase().includes(q);
      const matchesAsk = !activeAsk || a.ask === activeAsk;
      const matchesPriority = activePriorities.has(a.priority);
      return matchesText && matchesAsk && matchesPriority;
    });
  }, [data, search, activeAsk, activePriorities]);

  const toggleButtonStyle = (pressed: boolean) => ({
    ...type.bodySmallMedium,
    padding: "7px 12px",
    borderRadius: 8,
    border: `1px solid ${pressed ? colors.ink : colors.borderStrong}`,
    background: pressed ? colors.ink : colors.surface,
    color: pressed ? colors.bg : colors.inkMuted,
    cursor: "pointer",
  });

  if (!unlocked) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: colors.bg,
          fontFamily,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <Helmet>
          <title>Warm Path Directory | Markit AI</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <form
          onSubmit={handleUnlockSubmit}
          style={{
            width: "100%",
            maxWidth: 320,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <p style={{ ...type.bodySmallMedium, color: colors.inkFaint, margin: 0 }}>
            MARKIT · FUNDRAISING
          </p>
          <h1 style={{ ...type.sectionTitle, color: colors.ink, margin: "0 0 4px" }}>
            This page is password-protected
          </h1>
          <input
            type="password"
            value={passwordInput}
            onChange={(e) => {
              setPasswordInput(e.target.value);
              setPasswordError(false);
            }}
            placeholder="Password"
            autoFocus
            style={{
              ...type.bodyRegular,
              padding: "10px 12px",
              borderRadius: 8,
              border: `1px solid ${passwordError ? colors.looking : colors.borderStrong}`,
              background: colors.surface,
              color: colors.ink,
            }}
          />
          {passwordError && (
            <p style={{ ...type.bodySmallRegular, color: colors.looking, margin: 0 }}>
              Incorrect password.
            </p>
          )}
          <button
            type="submit"
            style={{
              ...type.bodyBold,
              padding: "10px 12px",
              borderRadius: 8,
              border: "none",
              background: colors.ink,
              color: colors.bg,
              cursor: "pointer",
            }}
          >
            Continue
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: colors.bg, fontFamily }}>
      <Helmet>
        <title>Warm Path Directory | Markit AI</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div
        style={{
          maxWidth: 780,
          margin: "0 auto",
          padding: "48px 24px 96px",
        }}
      >
        <p style={{ ...type.bodySmallMedium, color: colors.inkFaint, marginBottom: 12 }}>
          MARKIT · FUNDRAISING
        </p>
        <h1 style={{ ...type.largeHeading, color: colors.ink, margin: "0 0 8px" }}>
          Warm Path Directory
        </h1>
        <p style={{ ...type.bodyRegular, color: colors.inkMuted, maxWidth: 560, margin: 0 }}>
          Target angel investors and VC funds, live from the pipeline. Switch
          lists with the dropdown, then search or filter to find who to talk
          to next.
        </p>

        <div
          className="AlignedRow"
          style={{
            gap: 10,
            flexWrap: "wrap",
            marginTop: 28,
            marginBottom: 28,
            paddingBottom: 16,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <select
            value={view}
            onChange={(e) => changeView(e.target.value as View)}
            style={{
              ...type.bodyBold,
              padding: "8px 12px",
              borderRadius: 8,
              border: `1px solid ${colors.borderStrong}`,
              background: colors.surface,
              color: colors.ink,
              cursor: "pointer",
            }}
          >
            <option value="funds">VC Funds</option>
            <option value="angels">Angel Investors</option>
          </select>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by name, company, or city…"
            style={{
              ...type.bodyRegular,
              flex: 1,
              minWidth: 160,
              padding: "8px 12px",
              borderRadius: 8,
              border: `1px solid ${colors.borderStrong}`,
              background: colors.surface,
              color: colors.ink,
            }}
          />
          <button
            type="button"
            style={toggleButtonStyle(activeAsk === "looking")}
            onClick={() => setActiveAsk((prev) => (prev === "looking" ? null : "looking"))}
          >
            Looking for an intro
          </button>
          <button
            type="button"
            style={toggleButtonStyle(activeAsk === "have")}
            onClick={() => setActiveAsk((prev) => (prev === "have" ? null : "have"))}
          >
            Have one, want more
          </button>
          {view === "funds" && (
            <button
              type="button"
              style={toggleButtonStyle(activeAsk === "covered")}
              onClick={() => setActiveAsk((prev) => (prev === "covered" ? null : "covered"))}
            >
              Already covered
            </button>
          )}
        </div>

        <div className="AlignedRow" style={{ gap: 6, flexWrap: "wrap", marginBottom: 24 }}>
          <span style={{ ...type.bodySmallMedium, color: colors.inkFaint, marginRight: 4 }}>
            Priority:
          </span>
          {ALL_PRIORITIES.map((tier) => (
            <button
              key={tier}
              type="button"
              style={toggleButtonStyle(activePriorities.has(tier))}
              onClick={() => togglePriority(tier)}
            >
              {tier}
            </button>
          ))}
        </div>

        {loadError && (
          <p style={{ ...type.bodyRegular, color: colors.looking }}>
            Couldn&apos;t load the directory. Try refreshing.
          </p>
        )}

        {!data && !loadError && (
          <p style={{ ...type.bodyRegular, color: colors.inkMuted }}>Loading…</p>
        )}

        {data && (
          <div className="ColumnNormal" style={{ gap: 12 }}>
            {view === "funds"
              ? filteredFunds.map((fund) => (
                  <EntryCard
                    key={fund.name}
                    name={fund.name}
                    meta={fund.city}
                    ask={fund.ask}
                    priority={fund.priority}
                    rationale={fund.rationale}
                  >
                    {(fund.namedContacts.length > 0 || fund.generalIntroSource.length > 0) && (
                      <div
                        style={{
                          marginTop: 12,
                          paddingTop: 10,
                          borderTop: `1px dashed ${colors.border}`,
                        }}
                      >
                        {fund.namedContacts.length > 0 && (
                          <>
                            <p
                              style={{
                                ...type.bodySmallMedium,
                                color: colors.inkFaint,
                                textTransform: "uppercase",
                                letterSpacing: 0.4,
                                margin: "0 0 6px",
                              }}
                            >
                              Known contacts
                            </p>
                            <div className="AlignedRow" style={{ gap: 14, flexWrap: "wrap" }}>
                              {fund.namedContacts.map((c) => (
                                <span key={c.name} style={{ ...type.bodySmallRegular, color: colors.ink }}>
                                  {c.name}
                                  {c.introSource.length > 0 && (
                                    <span style={{ color: colors.inkMuted }}>
                                      {" "}— intro via {c.introSource.join(", ")}
                                    </span>
                                  )}
                                </span>
                              ))}
                            </div>
                          </>
                        )}
                        {fund.generalIntroSource.length > 0 && (
                          <p
                            style={{
                              ...type.bodySmallRegular,
                              color: colors.inkMuted,
                              fontStyle: "italic",
                              margin: "8px 0 0",
                            }}
                          >
                            General path in: {fund.generalIntroSource.join(", ")}
                          </p>
                        )}
                      </div>
                    )}
                  </EntryCard>
                ))
              : filteredAngels.map((angel) => (
                  <EntryCard
                    key={angel.name}
                    name={angel.name}
                    meta={angel.city && angel.org ? `${angel.org} · ${angel.city}` : angel.org || angel.city}
                    ask={angel.ask}
                    priority={angel.priority}
                    rationale={angel.rationale}
                  >
                    {angel.introSource.length > 0 && (
                      <p style={{ ...type.bodySmallRegular, color: colors.inkMuted, margin: "8px 0 0" }}>
                        Intro via {angel.introSource.join(", ")}
                      </p>
                    )}
                  </EntryCard>
                ))}
            {(view === "funds" ? filteredFunds : filteredAngels).length === 0 && (
              <p style={{ ...type.bodyRegular, color: colors.inkFaint, textAlign: "center", padding: "32px 0" }}>
                Nothing matches that filter.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default MarkitFundraisingScreen;
